import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

interface DocumentSectionProps {
  /** Vira a âncora (`#data-e-local`) e o alvo do `aria-labelledby`. */
  id: string;
  title: string;
  description?: ReactNode;
  /** Antes do título — o número da seção, no formulário. */
  titleLeading?: ReactNode;
  /** Depois do título — o crachá "opcional", no formulário. */
  titleTrailing?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Uma seção do documento de projeto — **a mesma gramática de quem escreve e de
 * quem lê** (spec-project-review-ux.md decisão 18).
 *
 * **Sem `Card`, de propósito.** Cinco cartões empilhados viram cinco caixinhas
 * e desmancham a leitura contínua que este documento existe para ter — o que
 * separa uma seção da outra é espaço e uma linha, como em um documento de
 * verdade. `Card` continua sendo a forma dos blocos DENTRO de uma seção.
 *
 * O docblock acima morava em `FormSection`, e desceu para cá porque é aqui que
 * ele passa a valer para as duas telas: a de escrita e a de leitura mostravam a
 * mesma entidade em três gramáticas diferentes (grid de fatos → cartões →
 * seções), e quem decide lia um documento que não se parecia com o que o autor
 * preencheu.
 *
 * `scroll-mt` existe porque a topbar do AppShell é fixa: sem a margem, clicar
 * numa âncora deixaria o cabeçalho da seção escondido atrás dela.
 */
export function DocumentSection({
  id,
  title,
  description,
  titleLeading,
  titleTrailing,
  children,
  className,
}: DocumentSectionProps) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-titulo`}
      className={cn(
        'scroll-mt-24 border-t border-border pt-8 first:border-t-0 first:pt-0',
        className,
      )}
    >
      <div className="mb-4 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          {titleLeading}
          <h2 id={`${id}-titulo`} className="text-lg font-semibold tracking-tight">
            {title}
          </h2>
          {titleTrailing}
        </div>
        {description ? <div className="text-sm text-muted-foreground">{description}</div> : null}
      </div>

      {children}
    </section>
  );
}
